import React, { useEffect, useReducer, useRef } from 'react';
import {
  Button, ButtonToolbar, Form, FormInstance, Loader, Stack, Toggle,
} from 'rsuite';
import { defaultTo } from 'lodash';
import axios from 'axios';
import { PartnerEshopPricingEnablerSetup } from '../../api/apiTypes';
import {
  Eshop,
  getPartnerEshopPricingEnablerSetup,
  savePartnerEshopPricingEnablerSetup,
} from '../../api/eshop/eshop';
import { showSuccessMessage } from '../../utils/message';

interface Props {
  eshop: Eshop;
}

interface Edit {
  enabled: boolean;
}

type Status = 'ready' | 'loading' | 'saving';

interface State {
  status: Status;
  setup?: PartnerEshopPricingEnablerSetup;
  edit: Edit;
}

const initialState: State = {
  status: 'ready',
  edit: {
    enabled: false,
  },
};

type StatusSetAction = { type: 'status/set', payload: Status };
type SetupSetAction = { type: 'setup/set', payload: PartnerEshopPricingEnablerSetup };
type EditSetAction = { type: 'edit/set', payload: Partial<Edit> };
type EditResetAction = { type: 'edit/reset' };
type Action = StatusSetAction
| SetupSetAction
| EditSetAction
| EditResetAction;

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'status/set': {
      return { ...state, status: action.payload };
    }
    case 'setup/set':
      return {
        ...state,
        setup: action.payload,
        edit: {
          ...state.edit,
          ...action.payload,
        },
      };
    case 'edit/set':
      return { ...state, edit: { ...state.edit, ...action.payload } };
    case 'edit/reset':
      return {
        ...state,
        edit: {
          enabled: defaultTo(
            state.setup?.enabled,
            initialState.edit.enabled,
          ),
        },
      };
    default:
      return state;
  }
}

function mapApiPricingSetup(edit: Edit, eshop: Eshop): PartnerEshopPricingEnablerSetup {
  return {
    ...edit,
    marketplaceCode: eshop.code,
  };
}

export default function EshopSetupPricing({ eshop }: Readonly<Props>): JSX.Element {
  const [state, dispatch] = useReducer(reducer, initialState);
  const abortControllerRef = useRef<AbortController>(new AbortController());
  const formRef = useRef<FormInstance>(null);

  async function fetchData(signal: AbortSignal): Promise<void> {
    dispatch({ type: 'status/set', payload: 'loading' });

    try {
      const setup = await getPartnerEshopPricingEnablerSetup(eshop.code, signal);
      dispatch({ type: 'setup/set', payload: setup });
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status !== 404) {
        throw error;
      }
    } finally {
      dispatch({ type: 'status/set', payload: 'ready' });
    }
  }

  async function saveSetup(): Promise<void> {
    if (abortControllerRef.current.signal.aborted) {
      abortControllerRef.current = new AbortController();
    }

    dispatch({ type: 'status/set', payload: 'saving' });

    try {
      await savePartnerEshopPricingEnablerSetup(
        mapApiPricingSetup(state.edit, eshop),
        abortControllerRef.current.signal,
      );
    } finally {
      dispatch({ type: 'status/set', payload: 'ready' });
      showSuccessMessage('Pricing setup saved.');
    }
  }

  function handleChange(edit: Record<string, any>): void {
    dispatch({ type: 'edit/set', payload: edit as Partial<Edit> });
  }

  function handleSave() {
    saveSetup().then();
  }

  function handleCancel(): void {
    dispatch({ type: 'edit/reset' });
  }

  useEffect(
    () => () => {
      abortControllerRef.current.abort();
    },
    [abortControllerRef.current],
  );

  useEffect(() => {
    const abortController = new AbortController();

    fetchData(abortController.signal).then();

    return () => {
      abortController.abort();
    };
  }, [eshop]);

  if (state.status === 'loading') {
    return <Loader />;
  }

  return (
    <Stack>
      <Form
        layout="horizontal"
        ref={formRef}
        formValue={state.edit}
        onChange={(edit) => handleChange(edit)}
      >
        <Form.Group>
          <Form.ControlLabel>
            Enable pricing
          </Form.ControlLabel>
          <Form.Control
            name="enabled"
            checked={state.edit.enabled}
            accepter={Toggle}
          />
        </Form.Group>
        <Form.Group>
          <ButtonToolbar>
            <Button
              appearance="primary"
              onClick={() => handleSave()}
              type="submit"
              loading={state.status === 'saving'}
            >
              Save
            </Button>
            <Button
              appearance="ghost"
              onClick={() => handleCancel()}
            >
              Cancel
            </Button>
          </ButtonToolbar>
        </Form.Group>
      </Form>
    </Stack>
  );
}
